import { Alert, Button, Card, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useState } from 'react';
import { endOfMonth, min } from 'date-fns';
import { earliestSelectableDay, latestSelectableDay, validateCustomRange } from './customCloseout';
import { closeoutTestId } from './test-ids';

type CustomPeriodBarProps = {
  onCreate: (from: Date, to: Date) => void;
  creating?: boolean;
  /** Server-side rejection (overlap, month already closed monthly, …). */
  serverError?: string | null;
  onDismissServerError?: () => void;
};

/**
 * BOPS-115 — the "New closeout period" bar above the custom closeouts table.
 *
 * Validation runs as the dates change rather than on submit: the mock shows Create greyed out with
 * the reason underneath, so the guardrail reads as "this range isn't allowed" instead of a click
 * that appears to do nothing. The To picker is also clamped to the From date's month, so the
 * one-month rule is mostly unreachable by clicking at all.
 */
const CustomPeriodBar: React.FC<CustomPeriodBarProps> = ({
  onCreate,
  creating = false,
  serverError = null,
  onDismissServerError,
}) => {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  //  Only complain once both dates exist — "Select both a From and a To date." as an error while
  //  the user is still filling the first field would be nagging rather than helpful.
  const validationError = from && to ? validateCustomRange(from, to) : null;

  //  BOPS-115 (8/7 decision) — a period must start and end today or in the past, so neither picker
  //  opens past today. To is additionally held inside From's month, as before; whichever bound
  //  comes first wins.
  const lastAllowedDay = latestSelectableDay();
  //  Matches the floor on the Years dropdown and the Custom range month picker — a period created
  //  below it could never be filtered back to afterwards.
  const firstAllowedDay = earliestSelectableDay();
  const toMaxDate = from ? min([endOfMonth(from), lastAllowedDay]) : lastAllowedDay;
  const canCreate = Boolean(from && to) && !validationError && !creating;

  const handleDateChange = (setter: (date: Date | null) => void) => (date: Date | null) => {
    setter(date);
    onDismissServerError?.();
  };

  const handleCreate = () => {
    if (!canCreate || !from || !to) return;
    onCreate(from, to);
  };

  //  The parent remounts this bar (via `key`) after a successful create, which clears the pickers —
  //  leaving the dates in place would invite an immediate duplicate submit.
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, mb: 2, borderRadius: '8px' }}
    >
      <Typography variant="subtitle2">New closeout period</Typography>
      <Typography
        variant="body2"
        sx={{ color: '#6C737F', mb: 1.5 }}
      >
        Pick a start and end date within a single calendar month.
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
          >
            <DatePicker
              label="From"
              value={from}
              minDate={firstAllowedDay}
              maxDate={lastAllowedDay}
              onChange={handleDateChange(setFrom)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 180 },
                  error: Boolean(validationError),
                  placeholder: 'Select date',
                  inputProps: { 'data-testid': closeoutTestId('create-period-from') },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: '#6C737F', display: { xs: 'none', sm: 'block' } }}
            >
              to
            </Typography>
            <DatePicker
              label="To"
              value={to}
              //  Bound to the From date's month so the picker itself enforces "within one month".
              minDate={from ?? firstAllowedDay}
              maxDate={toMaxDate}
              onChange={handleDateChange(setTo)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 180 },
                  error: Boolean(validationError),
                  placeholder: 'Select date',
                  inputProps: { 'data-testid': closeoutTestId('create-period-to') },
                },
              }}
            />
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={!canCreate}
            data-testid={closeoutTestId('create-period-submit')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {creating ? 'Creating…' : 'Create closeout period'}
          </Button>
        </Stack>
      </LocalizationProvider>
      {(validationError || serverError) && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon fontSize="small" />}
          sx={{
            mt: 2,
            py: 0.75,
            alignItems: 'center',
            borderRadius: '8px',
            border: '1px solid #FDA29B',
            backgroundColor: '#FFFBFA',
            color: '#B42318',
            '& .MuiAlert-icon': { color: '#D92D20' },
            '& .MuiAlert-message': { fontSize: '0.875rem', py: 0.5 },
          }}
        >
          {validationError || serverError}
        </Alert>
      )}
    </Card>
  );
};

export default CustomPeriodBar;
